import React, { useEffect, useMemo, useState } from "react";

import { useQueries } from "@tanstack/react-query";

import { AdminCoverCropDetection } from "services";

import { fetchFieldPracticesWithCropNames, useTranslation } from "utils";

import { GET_FIELD_PRACTICES } from "consts";

import { Slider, Typography } from "components";

import { AutomationCard } from "pages/admin/verifications/components/automations/AutomationCard";
import { countCoverCropChips, formatConfidence, getCoverCropChip, getTimelineYears } from "pages/admin/verifications/utils/coverCropChips";

const PRACTICES_STALE_TIME = 1000 * 60 * 60 * 2; // same as useGetFieldPractices on admin

type Props = {
  detections: AdminCoverCropDetection[];
  minConfidence: number;
  onMinConfidenceChange: (value: number) => void;
};

export const CoverCropAutomationCard = ({ detections, minConfidence, onMinConfidenceChange }: Props) => {
  const { t } = useTranslation(["practices"], { useSuspense: false });

  // The label follows the handle while dragging. The chips and counts update when the drag ends, so the whole
  // crops list doesn't re-render on every step.
  const [sliderValue, setSliderValue] = useState(Math.round(minConfidence * 100));
  useEffect(() => setSliderValue(Math.round(minConfidence * 100)), [minConfidence]);

  // Whether a result is "Confirmed" or "Missing" depends on the farmer's crops. These queries share the
  // crops tab's cache key, so fields the list already loaded don't trigger new requests.
  const fieldIds = useMemo(() => [...new Set(detections.map(({ fieldId }) => fieldId))], [detections]);
  const fetchPractices = (fieldId: string) => fetchFieldPracticesWithCropNames({ fieldId, isAdmin: true, t });
  const { cropsPerField, isLoadingCrops } = useQueries({
    queries: fieldIds.map((fieldId) => ({
      queryKey: [GET_FIELD_PRACTICES, fieldId, true],
      queryFn: () => fetchPractices(fieldId),
      staleTime: PRACTICES_STALE_TIME,
    })),
    combine: (results) => ({
      cropsPerField: results.map((result) => result.data?.crops ?? []),
      isLoadingCrops: results.some((result) => result.isLoading),
    }),
  });

  const counts = useMemo(() => {
    const cropsByField = new Map(fieldIds.map((fieldId, index) => [fieldId, cropsPerField[index] ?? []]));
    const years = new Set(getTimelineYears());

    return countCoverCropChips(
      detections
        .filter(({ seasonYear }) => years.has(seasonYear))
        .map((detection) =>
          getCoverCropChip(
            detection,
            (cropsByField.get(detection.fieldId) ?? []).filter(({ year }) => year === detection.seasonYear),
            minConfidence
          )
        )
    );
  }, [detections, fieldIds, cropsPerField, minConfidence]);

  const rows = [
    { label: "Detected cover crops", hint: "Found by satellite at or above the minimum confidence", value: counts.detected, className: "" },
    { label: "Confirmed cover crops", hint: "Farmer added the same crop", value: counts.confirmed, className: "confirmed" },
    { label: "Missing cover crops", hint: "Satellite only, farmer didn't add it", value: counts.missing, className: "" },
    { label: "Not detected cover crops", hint: "Farmer added it, satellite saw none", value: counts.notDetected, className: "not-detected" },
  ];

  return (
    <AutomationCard iconName="tabler/satellite" title="Remote sensing · cover crop" className="remote-sensing" testId="cover-crop-automation-card">
      <div className="cover-crop-confidence">
        <div className="cover-crop-confidence-label">
          <Typography variant="paragraph-s" color="grey-600">
            Minimum confidence
          </Typography>
          <Typography variant="paragraph-s" bold>
            {formatConfidence(sliderValue / 100)}
          </Typography>
        </div>
        <Slider
          min={0}
          max={100}
          step={1}
          value={sliderValue}
          onChange={setSliderValue}
          onChangeComplete={(value: number) => onMinConfidenceChange(value / 100)}
          tooltip={{ open: false }}
          ariaLabelForHandle="Minimum confidence"
        />
      </div>

      {rows.map(({ label, hint, value, className }) => (
        <div key={label} className={`cover-crop-count ${className}`}>
          <div>
            <Typography variant="paragraph-m" bold className="cover-crop-count-label">
              {label}
            </Typography>
            <Typography variant="paragraph-s" color="grey-600">
              {hint}
            </Typography>
          </div>
          <span className="cover-crop-count-value">{isLoadingCrops ? "-" : value}</span>
        </div>
      ))}

      <Typography variant="paragraph-xs" color="grey-600">
        Probabilistic. &quot;Not detected&quot; does not mean no cover crop.
      </Typography>
    </AutomationCard>
  );
};
